import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Utensils, Clock, Tag, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Subscriptions() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const { user, login } = useAuth();

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await fetch(`${API}/subscription-plans`);
        const data = await response.json();
        setPlans(data);
      } catch (error) {
        console.error('Error fetching plans:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPlans();
  }, []);

  const handleSubscribe = async (plan) => {
    if (!user) {
      login();
      return;
    }

    setProcessingPlan(plan.plan_id);

    try {
      // Create subscription
      const subResponse = await fetch(`${API}/subscriptions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ plan_id: plan.plan_id })
      });

      if (!subResponse.ok) {
        const error = await subResponse.json();
        throw new Error(error.detail || 'Error al crear la suscripción');
      }

      const subData = await subResponse.json();

      // Create checkout session
      const checkoutResponse = await fetch(`${API}/subscriptions/${subData.subscription_id}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          origin_url: window.location.origin
        })
      });

      if (!checkoutResponse.ok) {
        throw new Error('Error al procesar el pago');
      }

      const checkoutData = await checkoutResponse.json();
      window.location.href = checkoutData.checkout_url;

    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.message || 'Error al procesar tu suscripción');
    } finally {
      setProcessingPlan(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-emerald-700 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <Badge className="bg-orange-100 text-orange-700 border-0 mb-6 px-4 py-2 text-sm font-medium">
              Suscripciones Mensuales
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-6">
              Planes de <span className="text-emerald-700">Alimentación</span>
            </h1>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Elige el plan que mejor se adapte a tus necesidades y disfruta de comida fresca todos los días
            </p>
          </div>

          {/* Benefits */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              { icon: Utensils, title: 'Acceso al Menú Completo', desc: 'Todos nuestros productos disponibles' },
              { icon: Clock, title: 'Reserva Anticipada', desc: 'Asegura tu comida con tiempo' },
              { icon: Tag, title: 'Descuentos Exclusivos', desc: 'Precios especiales para suscriptores' }
            ].map((benefit, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-stone-100 text-center">
                <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-emerald-700" />
                </div>
                <h3 className="font-semibold text-stone-900 mb-2">{benefit.title}</h3>
                <p className="text-sm text-stone-500">{benefit.desc}</p>
              </div>
            ))}
          </div>

          {/* Plans */}
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan, index) => {
              const isPopular = index === 1;
              return (
                <div
                  key={plan.plan_id}
                  className={`relative bg-white rounded-3xl border-2 p-8 transition-all duration-300 hover:shadow-xl ${
                    isPopular ? 'border-emerald-700 shadow-lg' : 'border-stone-100'
                  }`}
                  data-testid={`plan-${plan.plan_id}`}
                >
                  {isPopular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <Badge className="bg-emerald-700 text-white border-0 px-4 py-1 text-sm">
                        <Sparkles className="w-3 h-3 mr-1 inline" />
                        Más Popular
                      </Badge>
                    </div>
                  )}

                  <div className="text-center mb-8">
                    <h3 className="text-xl font-bold text-stone-900 mb-2">{plan.name}</h3>
                    <p className="text-stone-500 text-sm mb-6">{plan.description}</p>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-5xl font-bold text-stone-900">${plan.price.toFixed(0)}</span>
                      <span className="text-stone-500">/mes</span>
                    </div>
                    <p className="text-orange-500 font-semibold mt-2">
                      {plan.meals_per_month} comidas incluidas
                    </p>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                          <Check className="w-3 h-3 text-emerald-700" />
                        </div>
                        <span className="text-stone-600 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className={`w-full rounded-full py-6 font-medium ${
                      isPopular
                        ? 'bg-emerald-700 hover:bg-emerald-800 text-white'
                        : 'bg-stone-100 hover:bg-stone-200 text-stone-900'
                    }`}
                    onClick={() => handleSubscribe(plan)}
                    disabled={processingPlan === plan.plan_id}
                    data-testid={`subscribe-${plan.plan_id}`}
                  >
                    {processingPlan === plan.plan_id ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="animate-spin w-5 h-5 border-2 border-current border-t-transparent rounded-full"></div>
                        Procesando...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        {user ? 'Suscribirse' : 'Iniciar Sesión'}
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>

          {/* FAQ Note */}
          <div className="mt-16 text-center">
            <p className="text-stone-500">
              ¿Tienes preguntas? <span className="text-emerald-700 font-medium cursor-pointer hover:underline">Contáctanos</span>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
